import { Link, useMatchRoute } from '@tanstack/react-router'
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Lightbulb,
  BarChart3,
  RefreshCw,
  Settings,
  Shield,
  ChevronLeft,
  Brain,
  Sparkles,
  ListChecks,
  Play,
  FileText,
  ClipboardList,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? 'dev'

interface NavItem {
  label: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
}

const clientNavBase: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: LayoutDashboard },
  { label: 'Usuarios', path: '/users', icon: Users },
  { label: 'Suscripciones', path: '/subscriptions', icon: CreditCard },
  { label: 'Recomendaciones', path: '/recommendations', icon: Lightbulb },
  { label: 'Historico', path: '/history', icon: BarChart3 },
  { label: 'Informes', path: '/reports', icon: FileText },
  { label: 'Sincronizaciones', path: '/sync-jobs', icon: RefreshCw },
  { label: 'Configuracion', path: '/settings', icon: Settings },
]

const aiRecommendationsItem: NavItem = {
  label: 'Recomendaciones IA', path: '/ai-recommendations', icon: Sparkles,
}

const adminNav: NavItem[] = [
  { label: 'Panel Global', path: '/admin', icon: Shield },
  { label: 'Clientes', path: '/admin/clients', icon: Building2 },
  { label: 'Tenants M365', path: '/admin/m365-tenants', icon: Building2 },
  { label: 'Usuarios', path: '/admin/users', icon: Users },
  { label: 'Auditoria', path: '/admin/audit', icon: ClipboardList },
]

const adminAiNav: NavItem[] = [
  { label: 'IA Dashboard', path: '/admin/ai', icon: Brain },
  { label: 'IA Tenants', path: '/admin/ai/tenants', icon: Building2 },
  { label: 'IA Contextos', path: '/admin/ai/contexts', icon: FileText },
  { label: 'IA Moderacion', path: '/admin/ai/moderation', icon: ListChecks },
  { label: 'IA Ejecuciones', path: '/admin/ai/runs', icon: Play },
  { label: 'IA Recomendaciones', path: '/admin/ai/recommendations', icon: Sparkles },
]

interface SidebarProps {
  userRole?: string
}

export function Sidebar({ userRole }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false)
  const matchRoute = useMatchRoute()

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', collapsed ? '72px' : '260px')
  }, [collapsed])

  // Check if AI recommendations feature is enabled for current tenant
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-recommendations', 'status'],
    queryFn: () => api.get<{ enabled: boolean; visibilityMode: string }>('/ai-recommendations/status'),
    staleTime: 60 * 1000,
    retry: false,
  })

  // Build client nav conditionally including AI item
  const clientNav: NavItem[] = aiStatus?.enabled
    ? [
        ...clientNavBase.slice(0, 4), // Dashboard, Usuarios, Suscripciones, Recomendaciones
        aiRecommendationsItem,
        ...clientNavBase.slice(4), // Historico, Sincronizaciones, Configuracion
      ]
    : clientNavBase

  const isActive = (path: string) => {
    if (path === '/') return matchRoute({ to: '/', fuzzy: false })
    return matchRoute({ to: path, fuzzy: true })
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col transition-all duration-300',
        collapsed ? 'w-[72px]' : 'w-[260px]',
      )}
      style={{ backgroundColor: 'var(--color-brand-primary)' }}
    >
      {/* Logo */}
      <div
        className="flex h-20 items-center justify-center px-5"
        style={{ backgroundColor: 'var(--color-brand-primary-700)' }}
      >
        {!collapsed ? (
          <img
            src="/ROI365_texto_linea.svg"
            alt="ROI365"
            className="h-12 w-full max-w-[180px] object-contain"
          />
        ) : (
          <span className="text-lg font-bold text-white">R</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {clientNav.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive(item.path)
                    ? 'bg-white/10 text-white'
                    : 'text-white/70 hover:bg-white/5 hover:text-white',
                )}
                style={
                  isActive(item.path)
                    ? { borderLeft: '3px solid var(--color-brand-accent-1)' }
                    : undefined
                }
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{item.label}</span>}
                {!collapsed && item.badge !== undefined && (
                  <span
                    className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold"
                    style={{
                      backgroundColor: 'var(--color-brand-accent-1)',
                      color: 'var(--color-brand-primary)',
                    }}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>

        {userRole === 'super_admin' && (
          <>
            <div className="my-4 border-t border-white/10" />
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-white/40">
              {!collapsed && 'Administracion'}
            </p>
            <ul className="space-y-1">
              {adminNav.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive(item.path)
                        ? 'bg-white/10 text-white'
                        : 'text-white/70 hover:bg-white/5 hover:text-white',
                    )}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="my-4 border-t border-white/10" />
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-white/40">
              {!collapsed && 'Recomendaciones IA'}
            </p>
            <ul className="space-y-1">
              {adminAiNav.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive(item.path)
                        ? 'bg-white/10 text-white'
                        : 'text-white/70 hover:bg-white/5 hover:text-white',
                    )}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* Version + Collapse toggle */}
      <div className="border-t border-white/10">
        {!collapsed && (
          <p className="px-3 pt-2 text-center text-[10px] tracking-wide text-white/25">
            {APP_VERSION}
          </p>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex h-10 w-full items-center justify-center text-white/50 hover:text-white"
        >
          <ChevronLeft
            className={cn('h-5 w-5 transition-transform', collapsed && 'rotate-180')}
          />
        </button>
      </div>
    </aside>
  )
}
